import { CommandPriority, ConcurrentQuery } from "./inverter.model";
import { Semaphore } from "./semaphore.utils";
import { ModbusError, ModbusErrorCode } from "./modbus.multi-request";
import { wait } from "./general.utils";
import { CircuitBreaker, CircuitBreakerConfig } from "./circuit-breaker";
import { DeviceRegisterMap } from "./device-register-map";
import { transportError } from "./device-error";
import { RetryPolicy, DEFAULT_RETRY_POLICY } from "./retry-policy";

const SCHEDULER_DEBUG = { ENABLED: true, LOG_CHUNKING: true, LOG_ENCODE_DECODE: true, LOG_PERF: true };
const _ss = (v: unknown) => v !== undefined && v !== null && typeof v === 'object' ? JSON.stringify(v) : (v ?? '');
const sLog = {
  info: (deviceId: string, msg: string, data?: unknown) => SCHEDULER_DEBUG.ENABLED && console.log(`[Scheduler:${deviceId}] ${msg}`, _ss(data)),
  chunk: (deviceId: string, msg: string, data?: unknown) => SCHEDULER_DEBUG.ENABLED && SCHEDULER_DEBUG.LOG_CHUNKING && console.log(`[Scheduler:${deviceId}] CHUNK: ${msg}`, _ss(data)),
  codec: (deviceId: string, msg: string, data?: unknown) => SCHEDULER_DEBUG.ENABLED && SCHEDULER_DEBUG.LOG_ENCODE_DECODE && console.log(`[Scheduler:${deviceId}] CODEC: ${msg}`, _ss(data)),
  perf: (deviceId: string, label: string) => {
    const start = performance.now();
    return { end: (details?: unknown) => SCHEDULER_DEBUG.ENABLED && SCHEDULER_DEBUG.LOG_PERF && console.log(`[Scheduler:${deviceId}] PERF: ${label} ${(performance.now() - start).toFixed(1)}ms`, _ss(details)) };
  },
};

/** Describes a single Modbus concurrency request. */
export interface ConcurrentRequestDescriptor {
  /** Array of raw commands. */
  requestsRaw: Uint8Array[];

  /** Priority for the semaphore. */
  priority: CommandPriority;

  /** Creation timestamp. */
  createdAt: Date;

  /** Concurrency settings for encoding/decoding. */
  concurrentQuery: ConcurrentQuery;

  /** Resolves sub-responses or rejects on error. */
  onResolve: (res: Uint8Array[] | Error) => void;

  /** Optional fatal error handler. */
  handleFatalError: (error?: any) => Promise<void>;

  /** Physical interaction handler. */
  interactionHandler: (cmd: Uint8Array) => Promise<Uint8Array>;

  /** Custom request timeout (ms). */
  maxTimeoutInMs: number;

  /** Estimated response size in bytes. */
  estimatedResponseSize?: number;

  /** Group name for batching (default if unspecified). */
  group?: string;

  /** Order index for grouped requests. */
  orderIndex?: number;

  /** Inverter version (3 = G3/type D, undefined = legacy). */
  inverterVersion?: number;

  /** Retry policy for this request (channel-specific). Falls back to DEFAULT_RETRY_POLICY. */
  retryPolicy?: RetryPolicy;

  /** If true, protocol errors (0x01-0x04) are non-fatal even on legacy inverters. */
  optional?: boolean;
}

/** Result of a single I/O attempt cycle (retry loop). */
interface AttemptResult {
  success: boolean;
  subResponses: Uint8Array[] | null;
  lastError: ModbusError | null;
  isFatalError: boolean;
  transportErrorForCB: ReturnType<typeof transportError> | null;
}

/** Schedules and processes concurrent Modbus requests. */
export class ScheduleModbusCommand {
  private static instances: Map<string, ScheduleModbusCommand> = new Map();
  private static readonly DEFAULT_KEY = '__default__';

  private readonly deviceId: string;
  private readonly semaphore: Semaphore;
  private readonly flushConcurrentRequestsSemaphore: Semaphore;
  private concurrentRequestsBuffer: ConcurrentRequestDescriptor[] = [];
  private bufferTimer: NodeJS.Timeout | null = null;

  private readonly bufferMaxSize = 1000;
  private readonly bufferTimeoutInMs = 500;
  private readonly delayBetweenCommandsInMs;
  private static readonly DEFAULT_MAX_COMPOSE_RESPONSE_SIZE = 512;
  private MAX_COMPOSE_RESPONSE_SIZE = ScheduleModbusCommand.DEFAULT_MAX_COMPOSE_RESPONSE_SIZE;
  private readonly MIN_COMPOSE_RESPONSE_SIZE = 1;

  /** Circuit breaker for the transport channel. Open = reject all requests immediately. */
  readonly circuitBreaker: CircuitBreaker;

  /** Per-device cache of registers that returned IllegalDataAddress/IllegalFunction.
   *  Keyed by full serial number — different FW versions may support different registers. */
  private get registerMap(): DeviceRegisterMap {
    return DeviceRegisterMap.forSerial(this.deviceId);
  }

  private constructor(
    delayBetweenCommandsInMs = 1,
    deviceId = ScheduleModbusCommand.DEFAULT_KEY,
    circuitBreakerConfig?: Partial<CircuitBreakerConfig>,
  ) {
    this.deviceId = deviceId;
    this.delayBetweenCommandsInMs = delayBetweenCommandsInMs;
    this.semaphore = new Semaphore(1, this.delayBetweenCommandsInMs);
    this.flushConcurrentRequestsSemaphore = new Semaphore(1, this.delayBetweenCommandsInMs);
    this.flushConcurrentRequests = this.flushConcurrentRequestsSemaphore.wrap(this.flushConcurrentRequests.bind(this));
    this.circuitBreaker = new CircuitBreaker(`scheduler:${deviceId}`, circuitBreakerConfig);
    sLog.info(deviceId, `Created instance (delay=${delayBetweenCommandsInMs}ms)`);
  }

  /** Splits requests into groups, ensuring each group stays within a max size. */
  private chunkBySize(
    requests: ConcurrentRequestDescriptor[],
    maxSize: number
  ): ConcurrentRequestDescriptor[][] {
    const result: ConcurrentRequestDescriptor[][] = [];
    let currentChunk: ConcurrentRequestDescriptor[] = [];
    let currentSize = 0;

    for (const desc of requests) {
      const estimate = desc.estimatedResponseSize ?? 0;
      if (currentChunk.length > 0 && currentSize + estimate > maxSize) {
        result.push(currentChunk);
        currentChunk = [desc];
        currentSize = estimate;
      } else {
        currentChunk.push(desc);
        currentSize += estimate;
      }
    }
    if (currentChunk.length > 0) {
      result.push(currentChunk);
    }
    return result;
  }

  // ────────────────────────────────────────────────────────────────
  // Registry (static)
  // ────────────────────────────────────────────────────────────────

  /** Returns the default shared instance (retrocompatible). */
  public static getInstance(delayBetweenCommandsInMs = 1): ScheduleModbusCommand {
    return ScheduleModbusCommand.getInstanceForDevice(
      ScheduleModbusCommand.DEFAULT_KEY,
      delayBetweenCommandsInMs,
    );
  }

  /** Returns the instance for a specific deviceId, creating it if needed. */
  public static getInstanceForDevice(
    deviceId: string,
    delayBetweenCommandsInMs = 1,
    circuitBreakerConfig?: Partial<CircuitBreakerConfig>,
  ): ScheduleModbusCommand {
    if (!ScheduleModbusCommand.instances.has(deviceId)) {
      ScheduleModbusCommand.instances.set(
        deviceId,
        new ScheduleModbusCommand(delayBetweenCommandsInMs, deviceId, circuitBreakerConfig),
      );
      sLog.info(deviceId, `Registry now has ${ScheduleModbusCommand.instances.size} instance(s)`);
    }
    return ScheduleModbusCommand.instances.get(deviceId)!;
  }

  /** Resets and removes the instance for a specific deviceId. */
  public static resetForDevice(deviceId: string): void {
    const instance = ScheduleModbusCommand.instances.get(deviceId);
    if (instance) {
      sLog.info(deviceId, `Resetting instance`);
      instance.resetState();
      ScheduleModbusCommand.instances.delete(deviceId);
    } else {
      sLog.info(deviceId, `resetForDevice - no instance found`);
    }
  }

  /** Resets all instances (global cleanup). */
  public static resetAll(): void {
    sLog.info('*', `Resetting ALL ${ScheduleModbusCommand.instances.size} instance(s)`);
    for (const [, instance] of ScheduleModbusCommand.instances) {
      instance.resetState();
    }
    ScheduleModbusCommand.instances.clear();
  }

  // ────────────────────────────────────────────────────────────────
  // Instance lifecycle
  // ────────────────────────────────────────────────────────────────

  /** Resets scheduler state (buffer, semaphore, timer, circuit breaker) without removing from registry. */
  public resetState() {
    sLog.info(this.deviceId, `resetState: buffer=${this.concurrentRequestsBuffer.length} pending, circuit=${this.circuitBreaker.state}`);
    this.semaphore.reset();
    this.flushConcurrentRequestsSemaphore.reset();
    this.circuitBreaker.reset();

    this.concurrentRequestsBuffer = [];
    // Note: registerMap is model-scoped and intentionally NOT cleared on reset.
    // If a ZM2 doesn't support register X, no ZM2 will — the knowledge persists.
    // This is a hardware characteristic, not user-specific — no cleanup needed on logout.
    if (this.bufferTimer) {
      clearTimeout(this.bufferTimer);
      this.bufferTimer = null;
    }
  }

  /**
   * @deprecated Use `ScheduleModbusCommand.resetForDevice(deviceId)` or `ModbusLink.reset()` instead.
   * Kept for retrocompatibility — resets this instance and removes it from the registry.
   */
  reset() {
    sLog.info(this.deviceId, `reset() called`);
    this.resetState();
    ScheduleModbusCommand.instances.delete(this.deviceId);
  }

  /**
   * Cancels the buffer timer and triggers an immediate flush.
   * Results are delivered via the onResolve callbacks (fire-and-forget).
   */
  public requestFlush(): void {
    if (this.concurrentRequestsBuffer.length === 0) return;
    if (this.bufferTimer) {
      clearTimeout(this.bufferTimer);
      this.bufferTimer = null;
    }
    this.flushConcurrentRequests().catch(() => { /* reset during flush — safe to ignore */ });
  }

  /** Executes a function through the semaphore with the given priority. */
  public execute<T>(fn: () => Promise<T>, priority: CommandPriority, id: any, createdAt: Date): Promise<T> {
    Object.defineProperty(fn, 'name', { value: 'mbQuery' });
    Object.defineProperty(fn, 'id', { value: id });
    Object.defineProperty(fn, 'createdAt', { value: createdAt });

    switch (priority) {
      case CommandPriority.High:
        return this.semaphore.callFunctionHighPriority(fn);
      case CommandPriority.Medium:
        return this.semaphore.callFunction(fn);
      case CommandPriority.Low:
        return this.semaphore.callFunctionLowPriority(fn);
      default:
        throw new Error('Invalid priority');
    }
  }

  // ────────────────────────────────────────────────────────────────
  // Error classification
  // ────────────────────────────────────────────────────────────────

  /** True for errors where the register simply doesn't exist — halving won't help. */
  private isSkippableProtocolError(err: ModbusError): boolean {
    return err.code === ModbusErrorCode.IllegalFunction
        || err.code === ModbusErrorCode.IllegalDataAddress;
  }

  /**
   * Classifies a ModbusError into an action the scheduler should take.
   *
   * ┌─────────────────────────┬─────────────────────┬─────────────────────┐
   * │ Error                   │ G3 / optional        │ Legacy              │
   * ├─────────────────────────┼─────────────────────┼─────────────────────┤
   * │ IllegalFunction (0x01)  │ skip + block addr   │ skip + block addr   │
   * │ IllegalDataAddress(0x02)│ skip + block addr   │ skip + block addr   │
   * │ IllegalDataValue (0x03) │ reject (non-fatal)  │ fatal               │
   * │ SlaveDeviceFailure(0x04)│ retry               │ fatal               │
   * │ SlaveDeviceBusy (0x06)  │ retry               │ retry               │
   * │ Timeout                 │ retry               │ retry               │
   * └─────────────────────────┴─────────────────────┴─────────────────────┘
   */
  private classifyError(err: ModbusError, isResilient: boolean): 'fatal' | 'skip' | 'reject' | 'halve' | 'retry' {
    switch (err.code) {
      case ModbusErrorCode.RequestFailed:
      case ModbusErrorCode.GatewayPathUnavailable:
      case ModbusErrorCode.GatewayTargetFailed:
      case ModbusErrorCode.ModbusError:
        return 'fatal';

      case ModbusErrorCode.BusError0500:
        return 'halve';

      case ModbusErrorCode.Timeout:
      case ModbusErrorCode.SlaveDeviceBusy:
        return 'retry';

      // 0x04: G3/optional devices may return this transiently (busy) — retry.
      // Legacy devices use this as a permanent failure signal.
      case ModbusErrorCode.SlaveDeviceFailure:
        return isResilient ? 'retry' : 'fatal';

      // 0x01/0x02: register or function doesn't exist — skip and block address.
      // Always non-fatal regardless of protocol: retrying or crashing won't change that.
      case ModbusErrorCode.IllegalFunction:
      case ModbusErrorCode.IllegalDataAddress:
        return 'skip';

      // 0x03: register exists but value is out of range.
      // G3/optional: reject without halving or blocking (transient data issue).
      // Legacy: fatal (unexpected on legacy firmware).
      case ModbusErrorCode.IllegalDataValue:
        return isResilient ? 'reject' : 'fatal';

      default:
        return 'retry';
    }
  }

  /**
   * Records start addresses from descriptors into the model-scoped register map.
   *
   * Only the page start address is blocked, not every register in the range.
   * This is an intentional approximation:
   * - Modbus IllegalDataAddress (0x02) is all-or-nothing for the entire request
   *   range — the device doesn't indicate which specific register failed.
   * - getMinRegisterPages is deterministic: same table + same maxRegisterPerPage
   *   always produces the same pages with the same start addresses.
   * - addConcurrentRequest checks the blocklist by page start address, so the
   *   same page will be skipped on subsequent reads.
   */
  private recordBlockedAddresses(descriptors: ConcurrentRequestDescriptor[]): void {
    for (const desc of descriptors) {
      for (const raw of desc.requestsRaw) {
        if (raw.length >= 4) {
          const startAddr = (raw[2] << 8) | raw[3];
          this.registerMap.markInvalid(startAddr);
          sLog.info(this.deviceId, `Blocked address 0x${startAddr.toString(16).toUpperCase().padStart(4, '0')} (total=${this.registerMap.invalidCount})`);
        }
      }
    }
  }

  // ────────────────────────────────────────────────────────────────
  // I/O: retry loop (pure — no side effects on descriptors)
  // ────────────────────────────────────────────────────────────────

  /**
   * Attempts to execute a combined Modbus command with retries.
   * Returns a structured result — does NOT resolve/reject descriptors.
   */
  private async attemptIO(
    multiCmd: Uint8Array,
    combinedCount: number,
    handler: (cmd: Uint8Array) => Promise<Uint8Array>,
    timeout: number,
    decodeFn: (resp: Uint8Array, subReqCount: number) => Uint8Array[],
    policy: RetryPolicy,
    isResilient: boolean,
    versionLabel: string,
  ): Promise<AttemptResult> {
    let subResponses: Uint8Array[] | null = null;
    let success = false;
    let isFatalError = false;
    let lastError: ModbusError | null = null;
    let transportErrorForCB: ReturnType<typeof transportError> | null = null;

    for (let attempt = 1; attempt <= policy.maxRetries + 1; attempt++) {
      // Check circuit breaker before each retry — if another operation opened
      // the circuit while we were retrying, stop immediately.
      if (attempt > 1 && this.circuitBreaker.state === 'OPEN') {
        sLog.info(this.deviceId, `Circuit breaker OPEN — aborting retry at attempt ${attempt}`);
        isFatalError = true;
        break;
      }

      try {
        const ioTimer = sLog.perf(this.deviceId, `executeCmd(attempt=${attempt}/${policy.maxRetries + 1})`);
        const response = await this.execute(
          () => Promise.race([
            ModbusError.request(handler, multiCmd),
            ModbusError.timeout(timeout),
          ]),
          CommandPriority.High,
          multiCmd,
          new Date()
        );
        ioTimer.end({ responseSize: response.length });

        sLog.codec(this.deviceId, `decode: response=${response.length}B → ${combinedCount} sub-responses`);
        subResponses = decodeFn(response, combinedCount);
        sLog.codec(this.deviceId, `decode OK: ${subResponses.length} sub-responses, sizes=[${subResponses.map(r => r.length).join(',')}]`);

        this.circuitBreaker.onSuccess();
        if (this.MAX_COMPOSE_RESPONSE_SIZE < ScheduleModbusCommand.DEFAULT_MAX_COMPOSE_RESPONSE_SIZE) {
          this.MAX_COMPOSE_RESPONSE_SIZE = ScheduleModbusCommand.DEFAULT_MAX_COMPOSE_RESPONSE_SIZE;
        }
        success = true;
        break;

      } catch (err) {
        // Wrap non-ModbusError (e.g. TypeError) to ensure lastError is always informative.
        if (!(err instanceof ModbusError)) {
          lastError = new ModbusError(
            err instanceof Error ? err.message : String(err),
            ModbusErrorCode.FatalError,
          );
          if (attempt > policy.maxRetries) { isFatalError = true; }
          continue;
        }

        lastError = err;
        const action = this.classifyError(err, isResilient);
        sLog.info(this.deviceId, `${versionLabel} error: ${ModbusErrorCode[err.code]} → ${action} (attempt ${attempt}/${policy.maxRetries + 1})`);

        // Track transport errors for circuit breaker — but DON'T call onFailure per attempt.
        // We call onFailure once after the retry loop to avoid premature circuit opening.
        if (err.code === ModbusErrorCode.Timeout) {
          transportErrorForCB = transportError('timeout', { deviceId: this.deviceId });
        } else if (err.code === ModbusErrorCode.RequestFailed) {
          transportErrorForCB = transportError('request-failed', { deviceId: this.deviceId });
        } else if (err.code === ModbusErrorCode.BusError0500) {
          transportErrorForCB = transportError('disconnected', { deviceId: this.deviceId });
        }

        switch (action) {
          case 'fatal':
            isFatalError = true;
            break; // exit switch → break exits loop (line below)

          case 'skip':
          case 'reject':
          case 'halve':
            break; // exit switch → break exits loop

          case 'retry':
            if (policy.shouldRetry(err, attempt, isResilient)) {
              const delay = policy.getDelayMs(attempt);
              sLog.info(this.deviceId, `retrying in ${delay}ms`);
              await wait(delay);
              continue; // next attempt
            }
            // Retries exhausted — G3 and optional reads degrade gracefully,
            // legacy treats persistent failures as fatal (channel likely dead).
            sLog.info(this.deviceId, `retries exhausted for ${ModbusErrorCode[err.code]}`);
            isFatalError = !isResilient;
            break;
        }
        break; // exit retry loop for fatal/skip/reject/halve/exhausted
      }
    }

    return { success, subResponses, lastError, isFatalError, transportErrorForCB };
  }

  // ────────────────────────────────────────────────────────────────
  // Failure disposition (resolves descriptors or throws fatal)
  // ────────────────────────────────────────────────────────────────

  /** Resolves all descriptors with the given error. */
  private resolveWithError(descriptors: ConcurrentRequestDescriptor[], error: ModbusError): void {
    for (const desc of descriptors) {
      desc.onResolve(error);
    }
  }

  /**
   * Handles a failed chunk: resolves descriptors gracefully, halves + recurses, or throws fatal.
   * Called only when attemptIO returned success=false.
   */
  private async handleChunkFailure(
    descriptors: ConcurrentRequestDescriptor[],
    result: AttemptResult,
    encodeFn: (requests: Uint8Array[]) => Uint8Array,
    decodeFn: (resp: Uint8Array, subReqCount: number) => Uint8Array[],
    currentMaxByteSize: number,
  ): Promise<void> {
    const { lastError, isFatalError } = result;
    const isAtMinBatchSize = currentMaxByteSize <= this.MIN_COMPOSE_RESPONSE_SIZE;
    const isOptional = descriptors[0].optional === true;

    // ── Recoverable: skip (0x01/0x02) → resolve + block address ──
    if (!isFatalError && lastError && this.isSkippableProtocolError(lastError)) {
      sLog.info(this.deviceId, `Skippable: ${ModbusErrorCode[lastError.code]} — resolving ${descriptors.length} desc(s)`);
      this.resolveWithError(descriptors, lastError);
      this.recordBlockedAddresses(descriptors);
      return;
    }

    // ── Recoverable: reject (0x03) → resolve without blocking ──
    if (!isFatalError && lastError && lastError.code === ModbusErrorCode.IllegalDataValue) {
      sLog.info(this.deviceId, `Reject: IllegalDataValue — resolving ${descriptors.length} desc(s)`);
      this.resolveWithError(descriptors, lastError);
      return;
    }

    // ── Recoverable at min batch → resolve, keep channel open ──
    if (!isFatalError && lastError && isAtMinBatchSize) {
      sLog.info(this.deviceId, `Non-fatal at min size — resolving with error, channel stays open`);
      this.resolveWithError(descriptors, lastError);
      return;
    }

    // ── Optional with retries exhausted (e.g. Timeout on G4 probe) → resolve immediately.
    //    Without this, a non-protocol error (like Timeout) would fall through to halving
    //    and cause up to 9 recursive retry rounds before resolving at min batch size. ──
    if (isOptional && !isFatalError && lastError) {
      sLog.info(this.deviceId, `Optional retries exhausted (${ModbusErrorCode[lastError.code]}) — resolving ${descriptors.length} desc(s)`);
      this.resolveWithError(descriptors, lastError);
      return;
    }

    // ── Fatal or min batch → crash the channel ──
    if (isFatalError || isAtMinBatchSize) {
      sLog.info(this.deviceId, `FATAL ERROR: isFatalError=${isFatalError}, maxByteSize=${currentMaxByteSize}, pendingBuffer=${this.concurrentRequestsBuffer.length}`);
      try {
        await descriptors[0].handleFatalError(lastError);
        sLog.info(this.deviceId, `handleFatalError completed`);
      } catch (fatalErr) {
        sLog.info(this.deviceId, `handleFatalError threw: ${fatalErr}`);
      }
      this.concurrentRequestsBuffer = [];
      this.semaphore.reset();
      sLog.info(this.deviceId, `cleaning up`);
      throw new ModbusError('Fatal error', ModbusErrorCode.FatalError);
    }

    // ── Halve batch and retry with smaller chunks ──
    const newSize = Math.floor(currentMaxByteSize / 2);
    if (newSize >= this.MIN_COMPOSE_RESPONSE_SIZE) {
      sLog.info(this.deviceId, `Halving concurrency to ${newSize}`);
      this.MAX_COMPOSE_RESPONSE_SIZE = newSize;
    }
    const chunks = this.chunkBySize(descriptors, newSize);
    for (const chunk of chunks) {
      await this.processSubChunk(chunk, encodeFn, decodeFn, newSize);
    }
  }

  // ────────────────────────────────────────────────────────────────
  // processSubChunk (orchestrator)
  // ────────────────────────────────────────────────────────────────

  /** Processes a subset of requests: attempt I/O → on failure handle → on success distribute. */
  private async processSubChunk(
    descriptors: ConcurrentRequestDescriptor[],
    encodeFn: (requests: Uint8Array[]) => Uint8Array,
    decodeFn: (resp: Uint8Array, subReqCount: number) => Uint8Array[],
    currentMaxByteSize: number
  ): Promise<void> {
    if (descriptors.length === 0) return;

    // ── Prepare ──
    const combinedRequests: Uint8Array[] = [];
    for (const desc of descriptors) {
      combinedRequests.push(...desc.requestsRaw);
    }
    const multiCmd = encodeFn(combinedRequests);

    const inverterVersion = descriptors[0].inverterVersion;
    const isG3 = inverterVersion !== undefined && inverterVersion >= 3;
    const isOptional = descriptors[0].optional === true;
    const isResilient = isG3 || isOptional;
    const versionLabel = isG3 ? `G3(v${inverterVersion})` : 'Legacy';

    sLog.codec(this.deviceId, `processSubChunk: ${descriptors.length} desc, ${combinedRequests.length} raw, encoded=${multiCmd.length}B, maxByteSize=${currentMaxByteSize}, ${versionLabel}`);
    const chunkTimer = sLog.perf(this.deviceId, `processSubChunk(${combinedRequests.length} cmds)`);

    // ── Attempt I/O with retries ──
    const policy = descriptors[0].retryPolicy ?? DEFAULT_RETRY_POLICY;
    const result = await this.attemptIO(
      multiCmd, combinedRequests.length,
      descriptors[0].interactionHandler, descriptors[0].maxTimeoutInMs,
      decodeFn, policy, isResilient, versionLabel,
    );

    // ── Report to circuit breaker (once per logical operation) ──
    if (!result.success && result.transportErrorForCB) {
      this.circuitBreaker.onFailure(result.transportErrorForCB);
    }

    // ── Failure → delegate to handleChunkFailure ──
    if (!result.success || !result.subResponses) {
      await this.handleChunkFailure(descriptors, result, encodeFn, decodeFn, currentMaxByteSize);
      return;
    }

    // ── Success → distribute responses to descriptors ──
    let subIndex = 0;
    for (let di = 0; di < descriptors.length; di++) {
      const desc = descriptors[di];
      const n = desc.requestsRaw.length;
      const slice = result.subResponses.slice(subIndex, subIndex + n);
      sLog.codec(this.deviceId, `  distribute desc[${di}]: ${n} raw → ${slice.length} response(s), sizes=[${slice.map(r => r.length).join(',')}]`);
      subIndex += n;
      desc.onResolve(slice);
    }
    chunkTimer.end({ descriptors: descriptors.length, success: true });
  }

  // ────────────────────────────────────────────────────────────────
  // Buffer: add + flush
  // ────────────────────────────────────────────────────────────────

  /** Adds a request to the buffer and returns a Promise that resolves with sub-responses. */
  public addConcurrentRequest(
    req: Omit<ConcurrentRequestDescriptor, 'onResolve'>
  ): Promise<Uint8Array[]> {
    // Circuit breaker: reject immediately if transport is known to be dead
    if (this.circuitBreaker.state === 'OPEN') {
      return Promise.reject(new ModbusError(
        `Circuit breaker OPEN for ${this.deviceId} — transport is down`,
        ModbusErrorCode.RequestFailed,
      ));
    }

    // Blocklist: reject immediately if all addresses are known-unsupported
    if (this.registerMap.invalidCount > 0) {
      const allBlocked = req.requestsRaw.every(raw => {
        if (raw.length >= 4) {
          const startAddr = (raw[2] << 8) | raw[3];
          return this.registerMap.isInvalid(startAddr);
        }
        return false;
      });
      if (allBlocked) {
        sLog.info(this.deviceId, 'Skipping request — all addresses blocked by model register map');
        return Promise.reject(new ModbusError(
          'Register address not supported by device',
          ModbusErrorCode.IllegalDataAddress,
        ));
      }
    }

    return new Promise<Uint8Array[]>((resolve, reject) => {
      const descriptor: ConcurrentRequestDescriptor = {
        ...req,
        onResolve: (res) => {
          if (res instanceof Error) {
            reject(res);
          } else {
            resolve(res);
          }
        },
      };
      this.concurrentRequestsBuffer.push(descriptor);
      sLog.info(this.deviceId, `addConcurrentRequest: buffer=${this.concurrentRequestsBuffer.length}, group="${req.group || 'default'}", estimatedSize=${req.estimatedResponseSize || 0}`);

      if (!this.bufferTimer) {
        this.bufferTimer = setTimeout(() => {
          this.flushConcurrentRequests().catch(() => { /* reset during flush — safe to ignore */ });
        }, this.bufferTimeoutInMs);
      }
      if (this.concurrentRequestsBuffer.length >= this.bufferMaxSize) {
        this.flushConcurrentRequests().catch(() => { /* reset during flush — safe to ignore */ });
      }
    });
  }

  /** Flushes the buffer: sort by orderIndex, group by encodeFn, chunk by size, process. */
  private async flushConcurrentRequests() {
    if (this.bufferTimer) {
      clearTimeout(this.bufferTimer);
      this.bufferTimer = null;
    }
    const requestsToProcess = this.concurrentRequestsBuffer.slice();
    this.concurrentRequestsBuffer = [];

    if (requestsToProcess.length === 0) return;

    sLog.info(this.deviceId, `flushConcurrentRequests: processing ${requestsToProcess.length} request(s)`);

    // Track which descriptors have been processed (prevents double-resolve on fatal).
    const processed = new Set<ConcurrentRequestDescriptor>();

    try {
      // Sort all requests by orderIndex (preserves per-group ordering).
      requestsToProcess.sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

      // Group by encodeFn (all requests sharing the same encoder go in one batch).
      const groupsByEncode = new Map<
        (requests: Uint8Array[]) => Uint8Array,
        ConcurrentRequestDescriptor[]
      >();
      for (const r of requestsToProcess) {
        const encodeFn = r.concurrentQuery.encodeMultiQuery;
        if (!groupsByEncode.has(encodeFn)) {
          groupsByEncode.set(encodeFn, []);
        }
        groupsByEncode.get(encodeFn)!.push(r);
      }

      sLog.chunk(this.deviceId, `${groupsByEncode.size} encode group(s)`);

      for (const [encodeFn, groupRequests] of groupsByEncode.entries()) {
        const decodeFn = groupRequests[0].concurrentQuery.decodeMultiQuery;
        const getMaxSize = () => {
          const fromCount = groupRequests.reduce((acc, r) => Math.min(acc, r.concurrentQuery.count), 999);
          return Math.min(fromCount, this.MAX_COMPOSE_RESPONSE_SIZE);
        };

        sLog.chunk(this.deviceId, `encodeGroup: ${groupRequests.length} request(s), maxResponseSize=${getMaxSize()}, totalEstimated=${groupRequests.reduce((s, r) => s + (r.estimatedResponseSize ?? 0), 0)}B`);

        const chunks = this.chunkBySize(groupRequests, getMaxSize());
        for (const chunk of chunks) {
          await this.processSubChunk(chunk, encodeFn, decodeFn, getMaxSize());
          // Mark descriptors as processed (for double-resolve prevention).
          for (const desc of chunk) {
            processed.add(desc);
          }
        }
      }
    } catch (err) {
      // Fatal error from processSubChunk — resolve only unprocessed descriptors.
      for (const r of requestsToProcess) {
        if (!processed.has(r)) {
          r.onResolve(err as Error);
        }
      }
    } finally {
      if (this.concurrentRequestsBuffer.length > 0) {
        this.bufferTimer = setTimeout(() => {
          this.flushConcurrentRequests().catch(() => { /* reset during flush — safe to ignore */ });
        }, this.bufferTimeoutInMs);
      }
    }
  }
}
